"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { PiLinkSimple, PiWarningCircle } from "react-icons/pi";

import { PAGES } from "@/configs/pages.config";
import { DEFAULT_LOCALE, getLocaleFromPathname } from "@/lib/i18n/locales";
import { withSeoLocalePath } from "@/lib/i18n/seo-paths";

type AuthPageShellProps = {
  page: "login" | "signup";
  fileUrl?: string;
  children: ReactNode;
};

const authPageCopy = {
  en: {
    linkReadyTitle: "Your source is ready",
    linkReadyDescription: "Finish your free account and we will open the workspace with your link prefilled.",
    loginSsoErrorTitle: "Google sign-in did not complete",
    loginSsoErrorDescription: "Try Google again or continue with email. We kept your destination intact.",
    signupSsoErrorTitle: "Google signup did not complete",
    signupSsoErrorDescription: "Try Google again or continue with email. We kept your destination intact.",
  },
  es: {
    linkReadyTitle: "Tu fuente esta lista",
    linkReadyDescription: "Completa tu cuenta gratuita y abriremos el espacio de trabajo con tu enlace precargado.",
    loginSsoErrorTitle: "El acceso con Google no se completo",
    loginSsoErrorDescription: "Prueba Google otra vez o continua con correo. Hemos conservado tu destino.",
    signupSsoErrorTitle: "El registro con Google no se completo",
    signupSsoErrorDescription: "Prueba Google otra vez o continua con correo. Hemos conservado tu destino.",
  },
  fr: {
    linkReadyTitle: "Votre source est prete",
    linkReadyDescription: "Terminez votre compte gratuit et nous ouvrirons l'espace de travail avec votre lien deja rempli.",
    loginSsoErrorTitle: "La connexion Google n'a pas abouti",
    loginSsoErrorDescription: "Reessayez avec Google ou continuez avec l'email. Votre destination a ete conservee.",
    signupSsoErrorTitle: "L'inscription Google n'a pas abouti",
    signupSsoErrorDescription: "Reessayez avec Google ou continuez avec l'email. Votre destination a ete conservee.",
  },
  de: {
    linkReadyTitle: "Deine Quelle ist bereit",
    linkReadyDescription: "Schliesse dein kostenloses Konto ab und wir oeffnen den Workspace mit bereits eingefuelltem Link.",
    loginSsoErrorTitle: "Google-Anmeldung wurde nicht abgeschlossen",
    loginSsoErrorDescription: "Versuche Google erneut oder nutze E-Mail. Dein Ziel wurde beibehalten.",
    signupSsoErrorTitle: "Google-Registrierung wurde nicht abgeschlossen",
    signupSsoErrorDescription: "Versuche Google erneut oder nutze E-Mail. Dein Ziel wurde beibehalten.",
  },
  it: {
    linkReadyTitle: "La tua fonte e pronta",
    linkReadyDescription: "Completa il tuo account gratuito e apriremo il workspace con il link gia compilato.",
    loginSsoErrorTitle: "L'accesso con Google non e stato completato",
    loginSsoErrorDescription: "Riprova con Google o continua con l'email. Abbiamo mantenuto la tua destinazione.",
    signupSsoErrorTitle: "La registrazione con Google non e stata completata",
    signupSsoErrorDescription: "Riprova con Google o continua con l'email. Abbiamo mantenuto la tua destinazione.",
  },
  pt: {
    linkReadyTitle: "A tua fonte esta pronta",
    linkReadyDescription: "Conclui a tua conta gratuita e abriremos o workspace com o link ja preenchido.",
    loginSsoErrorTitle: "O login com Google nao foi concluido",
    loginSsoErrorDescription: "Tenta o Google novamente ou continua com email. Mantivemos o teu destino.",
    signupSsoErrorTitle: "O registo com Google nao foi concluido",
    signupSsoErrorDescription: "Tenta o Google novamente ou continua com email. Mantivemos o teu destino.",
  },
} as const;

function formatAuthErrorDescription(rawError: string | null, fallback: string) {
  if (!rawError) {
    return fallback;
  }

  try {
    return decodeURIComponent(rawError).replace(/\+/g, " ").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  } catch {
    return fallback;
  }
}

export default function AuthPageShell({ page, fileUrl, children }: AuthPageShellProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [showCallbackWarning, setShowCallbackWarning] = useState(false);
  const locale = pathname ? getLocaleFromPathname(pathname) : DEFAULT_LOCALE;
  const copy = authPageCopy[locale] ?? authPageCopy[DEFAULT_LOCALE];
  const authRootPath = useMemo(() => withSeoLocalePath(locale, page === "login" ? PAGES.LOGIN.PATH : PAGES.SIGN_UP.PATH), [locale, page]);
  const rawError = searchParams.get("error_description") ?? searchParams.get("error");
  const isSsoCallbackPath = pathname?.startsWith(`${authRootPath}/sso-callback`) ?? false;
  const hasAuthError = Boolean(rawError) || showCallbackWarning;
  const authErrorTitle = page === "login" ? copy.loginSsoErrorTitle : copy.signupSsoErrorTitle;
  const authErrorFallbackDescription = page === "login" ? copy.loginSsoErrorDescription : copy.signupSsoErrorDescription;
  const authErrorDescription = formatAuthErrorDescription(rawError, authErrorFallbackDescription);

  useEffect(() => {
    if (!isSsoCallbackPath || rawError) {
      setShowCallbackWarning(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowCallbackWarning(true);
    }, 1500);

    return () => window.clearTimeout(timeoutId);
  }, [isSsoCallbackPath, rawError]);

  return (
    <div className="mx-auto flex max-w-screen-2xl flex-grow flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md space-y-4">
        {fileUrl ? (
          <div className="rounded-[1.5rem] border border-primary-200 bg-primary-50 px-4 py-4 text-sm text-gray-700 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-white p-2 text-primary shadow-sm">
                <PiLinkSimple className="h-4 w-4" />
              </div>
              <div>
                <p className="font-semibold text-black">{copy.linkReadyTitle}</p>
                <p className="mt-1 leading-6">{copy.linkReadyDescription}</p>
              </div>
            </div>
          </div>
        ) : null}

        {hasAuthError ? (
          <div className="rounded-[1.5rem] border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-4 text-sm text-[#991B1B] shadow-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-white p-2 text-[#DC2626] shadow-sm">
                <PiWarningCircle className="h-4 w-4" />
              </div>
              <div>
                <p className="font-semibold">{authErrorTitle}</p>
                <p className="mt-1 leading-6">{authErrorDescription}</p>
              </div>
            </div>
          </div>
        ) : null}

        {children}
      </div>
    </div>
  );
}
